"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { useTranslations } from "next-intl"
import { Link } from "@/i18n/navigation"
import { Mail, Check } from "lucide-react"
import { toast } from "sonner"

export function NewsletterSignup() {
  const [email, setEmail] = useState("")
  const [privacyConsent, setPrivacyConsent] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const t = useTranslations("newsletter")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const response = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || "Failed to subscribe")
      }

      setIsSuccess(true)
      setEmail("")
      setPrivacyConsent(false)
      toast.success(t("success"))

      // Reset success message after 5 seconds
      setTimeout(() => setIsSuccess(false), 5000)
    } catch (error) {
      console.error("Newsletter subscription error:", error)
      toast.error(
        error instanceof Error ? error.message : t("error") || "Failed to subscribe. Please try again."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card className="border-border bg-linear-to-br from-accent/5 to-background">
      <CardHeader>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-10 h-10 rounded-lg bg-linear-to-br from-accent to-accent-secondary flex items-center justify-center">
            <Mail className="h-5 w-5 text-accent-foreground" />
          </div>
          <CardTitle>{t("title")}</CardTitle>
        </div>
        <CardDescription>{t("description")}</CardDescription>
      </CardHeader>
      <CardContent>
        {isSuccess ? (
          <div className="flex items-center gap-2 text-accent">
            <Check className="h-5 w-5" />
            <p className="text-sm font-medium">{t("success")}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder={t("placeholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1"
                disabled={isSubmitting}
              />
              <Button
                type="submit"
                disabled={isSubmitting || !privacyConsent}
                className="whitespace-nowrap"
              >
                {isSubmitting ? t("submitting") : t("subscribe")}
              </Button>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox
                id="newsletter-privacy-consent"
                checked={privacyConsent}
                onCheckedChange={(checked) => setPrivacyConsent(checked === true)}
                required
                disabled={isSubmitting}
                className="mt-0.5"
              />
              <label
                htmlFor="newsletter-privacy-consent"
                className="text-xs text-foreground leading-relaxed cursor-pointer"
              >
                <span>{t("privacyConsentPrefix")}</span>{" "}
                <Link
                  href="/privacy"
                  className="text-accent hover:text-accent/90 underline underline-offset-4 font-medium"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t("privacyLinkText")}
                </Link>
                <span> {t("privacyConsentSuffix")}</span>
              </label>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  )
}

